/**
 * The **documented warm sets** for decoded sound residency (PRD #624 / issue #626):
 * which sound ids are decoded ahead of need, instead of the old
 * decode-the-whole-registry-on-first-gesture behaviour. Everything not in the
 * current warm set decodes lazily on first play (a millisecond-scale, one-time
 * cost) and is LRU-evictable once idle - see {@link ./soundResidency}.
 *
 * Four sets, one always-on + one per mode:
 * - {@link LOBBY_WARM_SOUNDS} decodes at **audio activation** (the first user
 *   gesture): the everyday town ambience every session hears within seconds.
 * - {@link ARENA_WARM_SOUNDS}, {@link SPHERE_WARM_SOUNDS}, and
 *   {@link CARD_WARM_SOUNDS} decode at **mode entry/readiness** (the run-active /
 *   match-active edge), so feature-critical cues are ready before first use.
 *
 * The current warm set the residency layer protects is always
 * `LOBBY_WARM_SOUNDS ∪ warmSoundsForMode(mode)` - leaving a mode releases its
 * set's protection (the members become ordinary LRU-evictable residents).
 * Membership here is a *residency* decision only; a sound outside every set still
 * plays fine, just via the lazy path.
 */
import type { SoundId } from './soundObserver';

/** A mode with a feature-critical warm set; `null` = lobby only. */
export type SoundWarmMode = 'arena' | 'sphere' | 'cards';

/**
 * The common-lobby set, decoded at audio activation: footsteps + doors (the
 * constant town ambience), the bank/award/collection one-shots, and the moongate
 * pair (gates appear unannounced beside the lobby). Deliberately small - the
 * point of this slice is that activation no longer decodes the whole registry.
 */
export const LOBBY_WARM_SOUNDS: readonly SoundId[] = [
  'footstep-a',
  'footstep-b',
  'door-open',
  'door-close',
  'sword7',
  'level-up',
  'collection-positive',
  'resurrect',
  'moongate-open',
  'moongate-enter',
];

/**
 * The Arena run's critical cues, warmed on the run-active edge: the fire-mage
 * spell FX, the archer's shot/impact set, the XP-orb economy plinks, the level-up
 * fanfare cap note, and the low-HP heartbeat. The bestiary's per-family monster
 * voices are deliberately *not* warmed - the set is large and each family lazy-
 * decodes on its first cue, then stays resident while the run keeps using it.
 */
export const ARENA_WARM_SOUNDS: readonly SoundId[] = [
  'spell-magic-arrow',
  'spell-fireball',
  'spell-explosion',
  'spell-flame-strike',
  'spell-poison',
  'spell-lightning',
  'spell-greater-heal',
  'spell-cure',
  'bow-shot',
  'crossbow-shot',
  'arrow-hit',
  'xp-pickup',
  'vacuum-grab',
  'level-up-fanfare',
  'heartbeat',
];

/**
 * Sphere Duel's critical cues, warmed on the match-active edge (which lands during
 * the `loading` ready-handshake phase, before combat begins): the sphere spellbook's
 * FX sounds and the famous fizzle blub.
 */
export const SPHERE_WARM_SOUNDS: readonly SoundId[] = [
  'sphere-fizzle',
  'spell-magic-arrow',
  'spell-flame-strike',
  'spell-lightning',
  'spell-poison',
  'spell-greater-heal',
  'spell-cure',
];

/**
 * Card battles reuse the classic spell vocabulary. Creature families stay lazy:
 * a summoned minion is visible for at least one presentation beat before it can
 * attack, while warming every bestiary family would defeat the decoded-byte cap.
 */
export const CARD_WARM_SOUNDS: readonly SoundId[] = [
  'card-duel-start',
  'card-your-turn',
  'sphere-fizzle',
  'poof',
  'spell-magic-arrow',
  'spell-fireball',
  'spell-explosion',
  'spell-flame-strike',
  'spell-lightning',
  'spell-greater-heal',
];

/** The mode-specific warm set for `mode` (`[]` for the lobby-only `null`). */
export function warmSoundsForMode(mode: SoundWarmMode | null): readonly SoundId[] {
  if (mode === 'arena') return ARENA_WARM_SOUNDS;
  if (mode === 'sphere') return SPHERE_WARM_SOUNDS;
  if (mode === 'cards') return CARD_WARM_SOUNDS;
  return [];
}
